/* =========================================================
   AF Quick Add Modal (MERGED: main + patch) + NO LAYOUT SHIFT (BODY + HEADER)
   File: assets/af-quick-add-modal.js

   Includes:
   - Only-one-open modal enforcement
   - Portal-to-body support (when our custom element is used)
   - Safe closest() for event targets (Text nodes / non-Elements)
   - Forces product fetch to ?view=quick-add
   - Prefetch (throttled) + LRU-ish cache with a 60s TTL
   - Warms hero images
   - Scroll-lock that prevents desktop layout shift:
       * adds body padding-right = scrollbar width
       * ALSO pads Dawn header wrappers to prevent header jump
       * unlock backstop for native modals too (no “page stuck”)

   Sep 2026 redesign (spec 2026-09-25-pdp-redesign-design.md, pass 2):
   - hide() waits while __afHoldUntil is in the future (af-quick-add.js sets
     it after an add, so the "ADDED ✓" button is seen before the close)
   - hide() plays a short exit (fade; the phone sheet slides down) before
     the real close; the back gesture closes at once
   - focus is kept inside the open modal (Dawn's trapFocus)

   ✅ Mobile back/gesture wiring (hash sentinel) — FIXED:
   - Open modal => push #af_qa sentinel ONLY if we are the ones who created it
   - Back gesture => hash disappears => close modal
   - Close button => removes sentinel WITHOUT navigating to previous page
     (uses history.back() only when we actually pushed our own entry,
      otherwise uses replaceState to drop the hash safely)
   ========================================================= */

(() => {
  if (window.__afQuickAddModalInit) return;
  window.__afQuickAddModalInit = true;

  /* =========================================================
     Mobile hash-based history (gesture-safe)
     ========================================================= */
  const mqDesktop = window.matchMedia("(min-width: 990px)");
  const isDesktop = () => mqDesktop.matches;

  // No URL change — prevents GA4/Meta false page_view events
  const HIST_TOKEN = `af_qa_${Math.random().toString(36).slice(2)}`;

  function closeAllQuickAddModals(fromBackGesture) {
    document.querySelectorAll('quick-add-modal.af-quickadd[open]').forEach((m) => {
      try {
        // already playing its exit (hide() itself stepped history back)
        if (m.__afExitTimer) return;
        if (fromBackGesture) {
          if (typeof m.__afHideInternal === "function") {
            m.__afHideInternal(true);
            return;
          }
          if (typeof m._hideInternal === "function") {
            m._hideInternal(true);
            return;
          }
        }

        if (typeof m.hide === "function") m.hide();
        else m.removeAttribute("open");
      } catch (e) {}
    });
  }

  const QA_HISTORY = {
    armed: false,
    pushed: false,
    suppressDisarm: false,

    _stateLooksOurs() {
      try {
        return !!(history.state && history.state.__af === HIST_TOKEN);
      } catch {
        return false;
      }
    },

    arm() {
      if (isDesktop()) return;

      if (this._stateLooksOurs()) {
        this.armed = true;
        this.pushed = false;
        return;
      }

      try {
        // Push state WITHOUT URL change — prevents GA4/Meta false page views
        history.pushState({ __af: HIST_TOKEN }, "");
        this.armed = true;
        this.pushed = true;
      } catch (e) {
        this.armed = false;
        this.pushed = false;
      }
    },

    disarmSafely() {
      if (isDesktop()) return false;
      if (!this.armed) return false;
      if (!this.pushed || !this._stateLooksOurs()) {
        this.clearSessionFlags();
        return false;
      }

      try {
        history.back();
        return true;
      } catch (e) {
        this.clearSessionFlags();
        return false;
      }
    },

    onPopState() {
      if (isDesktop()) return;

      const anyOpen = !!document.querySelector('quick-add-modal.af-quickadd[open]');
      if (!anyOpen) return;

      // Our state was popped — close modals
      if (!this._stateLooksOurs()) {
        closeAllQuickAddModals(true);
        this.clearSessionFlags();
      }
    },

    clearSessionFlags() {
      this.armed = false;
      this.pushed = false;
    },
  };

  window.addEventListener("popstate", () => QA_HISTORY.onPopState());

  /* =========================================================
     Scroll lock (prevents layout shift by compensating scrollbar width)
     ========================================================= */
  const AF_SCROLLLOCK = {
    _applied: false,
    _addedOverflowHidden: false,
    _prev: null,

    _headerEls() {
      return Array.from(
        document.querySelectorAll(
          "sticky-header, #shopify-section-header, .shopify-section-header, header.header, .header-wrapper"
        )
      );
    },

    lock() {
      if (this._applied) return;

      const html = document.documentElement;
      const body = document.body;

      const sbw = window.innerWidth - html.clientWidth;

      this._prev = {
        bodyPadRight: body.style.paddingRight || "",
        bodyOverscroll: body.style.overscrollBehavior || "",
        htmlOverscroll: html.style.overscrollBehavior || "",
        headerPads: new Map(),
      };

      this._addedOverflowHidden = !html.classList.contains("overflow-hidden");

      if (sbw > 0) {
        const pad = `${sbw}px`;
        body.style.paddingRight = pad;

        for (const el of this._headerEls()) {
          this._prev.headerPads.set(el, el.style.paddingRight || "");
          el.style.paddingRight = pad;
        }
      }

      body.style.overscrollBehavior = "contain";
      html.style.overscrollBehavior = "contain";

      html.classList.add("overflow-hidden");

      this._applied = true;
    },

    unlock() {
      if (!this._applied) return;

      const html = document.documentElement;
      const body = document.body;

      if (this._addedOverflowHidden) html.classList.remove("overflow-hidden");

      if (this._prev) {
        body.style.paddingRight = this._prev.bodyPadRight;
        body.style.overscrollBehavior = this._prev.bodyOverscroll;
        html.style.overscrollBehavior = this._prev.htmlOverscroll;

        for (const [el, prevPad] of this._prev.headerPads.entries()) {
          if (el && el.style) el.style.paddingRight = prevPad;
        }
      }

      this._applied = false;
      this._addedOverflowHidden = false;
      this._prev = null;
    },
  };

  /* =========================================================
     Only one modal open at a time
     ========================================================= */
  function closeOthers(except) {
    document.querySelectorAll("quick-add-modal.af-quickadd[open]").forEach((m) => {
      if (m === except) return;

      if (typeof m.hide === "function") {
        m.hide();
      } else {
        m.removeAttribute("open");
      }
    });
  }

  /* =========================================================
     Safe closest() for event targets
     ========================================================= */
  const closestFromEvent = (e, selector) => {
    let t = e && e.target;
    if (t && t.nodeType === 3) t = t.parentElement;
    if (!(t instanceof Element)) return null;
    return t.closest(selector);
  };

  /* =========================================================
     Helpers
     ========================================================= */
  const getContentWrap = (modal) =>
    modal.querySelector('[id^="QuickAddInfo-"]') ||
    modal.querySelector("#QuickAddInfo") ||
    modal.querySelector(".quick-add-modal__content-info") ||
    modal;

  const getDialog = (modal) =>
    modal.querySelector('[role="dialog"]') ||
    modal.querySelector(".quick-add-modal__content") ||
    modal;

  // Dawn's trapFocus fixes first/last when called: here "first" was Dawn's hidden close
  // button and Buy it now renders later, so Tab stuck on Add to cart. Instead, send focus
  // back in whenever it leaves: Tab past the end lands on the first visible control,
  // Shift+Tab past the start on the last.
  const visibleFocusables = (el) =>
    (typeof getFocusableElements === "function" ? getFocusableElements(el) : []).filter(
      (n) => n.getClientRects().length
    );

  const releaseQuickAddFocus = (modal) => {
    if (!modal.__afQaTrap) return;
    document.removeEventListener("focusin", modal.__afQaTrap.focusin);
    document.removeEventListener("keydown", modal.__afQaTrap.keydown, true);
    modal.__afQaTrap = null;
  };

  const trapQuickAddFocus = (modal, dialog) => {
    releaseQuickAddFocus(modal);
    if (typeof removeTrapFocus === "function") {
      try { removeTrapFocus(); } catch (e) {}
    }
    let backwards = false;
    const trap = {
      keydown: (e) => {
        if (e.key === "Tab") backwards = e.shiftKey;
      },
      focusin: (e) => {
        if (!modal.hasAttribute("open") || modal.contains(e.target)) return;
        // another overlay opened on top (size guide, drawer): leave its focus alone
        if (e.target.closest && e.target.closest('dialog, [role="dialog"], [aria-modal="true"], cart-drawer')) return;
        const els = visibleFocusables(dialog);
        const to = backwards ? els[els.length - 1] : els[0];
        (to || dialog).focus({ preventScroll: true });
      },
    };
    modal.__afQaTrap = trap;
    document.addEventListener("keydown", trap.keydown, true);
    document.addEventListener("focusin", trap.focusin);
  };

  const withQuickAddView = (url) => {
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set("view", "quick-add");
      return u.toString();
    } catch (e) {
      const s = String(url || "");
      if (s.includes("view=quick-add")) return s;
      return s + (s.includes("?") ? "&" : "?") + "view=quick-add";
    }
  };

  /* =========================================================
     Prefetch + cache
     ========================================================= */
  const MAX_CACHE = 40;
  // A fetched view is reused for this long, then refetched for fresh stock.
  // (It used to be purged on every close, so reopening always waited.)
  const CACHE_TTL = 60000;
  const qaCache = new Map(); // url -> { p: Promise<string>, t: fetch start, done }

  const fetchHtml = (url, priority) =>
    fetch(url, {
      credentials: "same-origin",
      headers: { "X-Requested-With": "XMLHttpRequest" },
      cache: "no-store",
      priority: priority || "auto",
    }).then((res) => {
      if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
      return res.text();
    });

  const touchCacheKey = (url) => {
    if (!qaCache.has(url)) return;
    const val = qaCache.get(url);
    qaCache.delete(url);
    qaCache.set(url, val);
  };

  const enforceCacheLimit = () => {
    while (qaCache.size > MAX_CACHE) {
      const firstKey = qaCache.keys().next().value;
      qaCache.delete(firstKey);
    }
  };

  const freshEntry = (url) => {
    const hit = qaCache.get(url);
    return hit && Date.now() - hit.t < CACHE_TTL ? hit : null;
  };

  const getHtml = (url, priority) => {
    if (!url) return Promise.reject(new Error("Missing URL"));

    const hit = freshEntry(url);
    if (hit) {
      touchCacheKey(url);
      return hit.p;
    }

    const entry = { t: Date.now(), done: false };
    entry.p = fetchHtml(url, priority).then(
      (html) => {
        entry.done = true;
        return html;
      },
      (err) => {
        if (qaCache.get(url) === entry) qaCache.delete(url);
        throw err;
      }
    );

    qaCache.delete(url);
    qaCache.set(url, entry);
    enforceCacheLimit();
    return entry.p;
  };

  const warmImagesFromDoc = (doc) => {
    try {
      const imgs = Array.from(doc.images || []).slice(0, 3);
      for (const img of imgs) {
        const src = img.currentSrc || img.src || img.getAttribute("src") || "";
        if (!src) continue;
        const pre = new Image();
        pre.decoding = "async";
        pre.loading = "eager";
        pre.src = src;
      }
    } catch (e) {}
  };

  const btnUrl = (btn) => {
    const rawUrl = btn?.dataset?.productUrl;
    return rawUrl ? withQuickAddView(rawUrl) : "";
  };

  const prefetchFromBtn = (btn, priority) => {
    const url = btnUrl(btn);
    if (!url || freshEntry(url)) return Promise.resolve();

    return getHtml(url, priority)
      .then((html) => {
        try {
          const doc = new DOMParser().parseFromString(html, "text/html");
          warmImagesFromDoc(doc);
        } catch (e) {}
      })
      .catch(() => {});
  };

  // Resolves true if p settles within ms, false otherwise (never rejects).
  const settlesWithin = (p, ms) =>
    new Promise((resolve) => {
      const t = setTimeout(() => resolve(false), ms);
      p.then(
        () => { clearTimeout(t); resolve(true); },
        () => { clearTimeout(t); resolve(true); }
      );
    });

  // Latest show() wins: a tap on another product while one is still loading.
  let showToken = 0;
  const OPEN_WAIT = 450;

  async function loadIntoModal(modal, openerBtn) {
    const contentWrap = getContentWrap(modal);
    const dialog = getDialog(modal);

    const rawUrl = openerBtn?.dataset?.productUrl;
    if (!rawUrl) {
      console.warn("[AF Quick Add] Missing data-product-url");
      return;
    }

    const url = withQuickAddView(rawUrl);

    if (modal.__afQaLoadedUrl === url && modal.hasAttribute("open")) {
      // Verify content is actually present — Dawn's hide() clears innerHTML
      // without resetting __afQaLoadedUrl, so this check can be stale.
      const hasContent = contentWrap.querySelector('.af-qa-view');
      if (hasContent) {
        try {
          dialog.focus({ preventScroll: true });
        } catch (e) {}
        return;
      }
    }
    modal.__afQaLoadedUrl = url;

    // token to prevent late injections after close/switch
    const reqId = (modal.__afQaReqId = (modal.__afQaReqId || 0) + 1);

    contentWrap.setAttribute("aria-busy", "true");
    contentWrap.innerHTML = `<div class="quick-add-modal__loader" aria-hidden="true"></div>`;

    try {
      const html = await getHtml(url);
      const doc = new DOMParser().parseFromString(html, "text/html");

      const view = doc.querySelector(".af-qa-view");
      if (!view) {
        console.warn("[AF Quick Add] .af-qa-view not found in response");
        contentWrap.innerHTML = `<div class="af-qa-error">Quick Add view not found.</div>`;
        return;
      }

      // If modal was closed or another product was opened while we were loading, drop this response.
      if (!modal.hasAttribute("open") || modal.__afQaLoadedUrl !== url || modal.__afQaReqId !== reqId) {
        return;
      }

      contentWrap.innerHTML = "";
      contentWrap.appendChild(view);

      // keep Tab inside the modal (aria-modal); show() then focuses the dialog itself
      trapQuickAddFocus(modal, dialog);

      try {
        const injectedHero =
          contentWrap.querySelector(".af-qa-media img") || contentWrap.querySelector("img");
        const url2 = (injectedHero && (injectedHero.currentSrc || injectedHero.src)) || "";
        if (url2) {
          const pre2 = new Image();
          pre2.decoding = "async";
          pre2.loading = "eager";
          pre2.src = url2;
        }
      } catch (e) {}

      modal.dispatchEvent(new CustomEvent("af:quickadd:loaded", { bubbles: true, detail: { url } }));

      try {
        dialog.focus({ preventScroll: true });
      } catch (e) {}
    } catch (err) {
      console.error("[AF Quick Add] Failed:", err);
      contentWrap.innerHTML = `<div class="af-qa-error">Couldn’t load Quick Add.</div>`;
    } finally {
      contentWrap.removeAttribute("aria-busy");
    }
  }

  /* =========================================================
     Custom element definition
     ========================================================= */
  if (!customElements.get("quick-add-modal")) {
    customElements.define(
      "quick-add-modal",
      class QuickAddModal extends HTMLElement {
        constructor() {
          super();

          this.contentWrap =
            this.querySelector('[id^="QuickAddInfo-"]') ||
            this.querySelector(".quick-add-modal__content-info") ||
            this;

          this.dialog =
            this.querySelector('[role="dialog"]') ||
            this.querySelector(".quick-add-modal__content") ||
            this;

          this.closeBtn =
            this.querySelector('[id^="ModalClose-"]') ||
            this.querySelector(".quick-add-modal__toggle");

          this._openedBy = null;

          this._placeholder = document.createComment("af-quickadd-placeholder");
          this._origParent = null;
          this._origNext = null;

          this.onKeyDown = this.onKeyDown.bind(this);
          this.onOverlayClick = this.onOverlayClick.bind(this);
          this.onCloseClick = this.onCloseClick.bind(this);

          this.__afQaManaged = true;

          // internal hide hook for hashchange close
          this.__afHideInternal = (fromBackGesture) => this._hideInternal(!!fromBackGesture);
        }

        connectedCallback() {
          // The wishlist builds this with createElement + innerHTML, so the constructor
          // ran before the shell existed. Resolve it now, or hide() would wipe the shell.
          this.contentWrap = getContentWrap(this);
          this.dialog = getDialog(this);
          this.closeBtn =
            this.querySelector('[id^="ModalClose-"]') ||
            this.querySelector(".quick-add-modal__toggle");

          if (this.closeBtn) this.closeBtn.addEventListener("click", this.onCloseClick);
          this.addEventListener("click", this.onOverlayClick);
        }

        disconnectedCallback() {
          if (this.closeBtn) this.closeBtn.removeEventListener("click", this.onCloseClick);
          this.removeEventListener("click", this.onOverlayClick);
          document.removeEventListener("keydown", this.onKeyDown);
        }

        onCloseClick(e) {
          e.preventDefault();
          this.hide();
        }

        onOverlayClick(e) {
          // Close if click landed outside the dialog card (not just on `this` exactly)
          const card = this.querySelector('.quick-add-modal__content');
          if (card && card.contains(e.target)) return;
          this.hide();
        }

        onKeyDown(e) {
          if (e.key === "Escape") this.hide();
        }

        _portalToBody() {
          if (this.parentNode === document.body) return;

          this._origParent = this.parentNode;
          this._origNext = this.nextSibling;

          this._origParent.insertBefore(this._placeholder, this._origNext);
          document.body.appendChild(this);
        }

        _restoreFromBody() {
          if (!this._origParent) return;

          if (this.parentNode === document.body) {
            this._origParent.insertBefore(this, this._placeholder);
            this._placeholder.remove();
          }

          this._origParent = null;
          this._origNext = null;
        }

        async show(opener) {
          const tok = ++showToken;

          // Open with the product already inside so the sheet slides in at its
          // full height; only a slow fetch (> OPEN_WAIT) opens on the loader.
          const url = btnUrl(opener);
          if (url) {
            const hit = freshEntry(url);
            if (!(hit && hit.done)) {
              const p = getHtml(url, "high");
              opener.setAttribute("aria-busy", "true");
              await settlesWithin(p, OPEN_WAIT);
              opener.removeAttribute("aria-busy");
              if (tok !== showToken) return;
            }
          }

          this._openedBy = opener || null;

          QA_HISTORY.suppressDisarm = true;
          closeOthers(this);
          QA_HISTORY.suppressDisarm = false;

          this._portalToBody();

          // Lock scroll BEFORE showing — prevents jerk from scrollbar disappearing
          // after the modal is already visible
          AF_SCROLLLOCK.lock();

          this.setAttribute("open", "");
          document.addEventListener("keydown", this.onKeyDown);

          // ✅ arm sentinel (mobile only)
          QA_HISTORY.arm();

          await loadIntoModal(this, opener);

          try {
            this.dialog.focus({ preventScroll: true });
          } catch (e) {}
        }

        _hideInternal(fromBackGesture) {
          // invalidate any in-flight loads
          this.__afQaReqId = (this.__afQaReqId || 0) + 1;

          // a pending hold / exit animation is superseded by this close
          clearTimeout(this.__afHoldTimer);
          clearTimeout(this.__afExitTimer);
          this.__afHoldTimer = 0;
          this.__afExitTimer = 0;
          this.__afHoldUntil = 0;
          this.classList.remove("is-closing");
          const card = this.querySelector(".quick-add-modal__content");
          if (card) {
            card.style.removeProperty("translate"); // phone drag-to-close
            card.style.removeProperty("transition");
          }
          if (typeof removeTrapFocus === "function") {
            try { removeTrapFocus(); } catch (e) {}
          }
          releaseQuickAddFocus(this);

          this.removeAttribute("open");
          document.removeEventListener("keydown", this.onKeyDown);

          // Clear stale DOM (payment iframes, listeners) so they don't accumulate
          if (this.contentWrap !== this) this.contentWrap.innerHTML = "";
          this.__afQaLoadedUrl = null;

          AF_SCROLLLOCK.unlock();

          // Notify listeners (product-form.js waits for this to render cart contents)
          document.body.dispatchEvent(new CustomEvent("modalClosed"));

          if (this._openedBy && typeof this._openedBy.focus === "function") {
            try {
              this._openedBy.focus({ preventScroll: true });
            } catch (e) {
              try { this._openedBy.focus(); } catch {}
            }
          }
          this._openedBy = null;

          try {
            this._restoreFromBody();
          } catch (e) {
            // Portal restore can fail if placeholder was removed (e.g. grid DOM recycling).
            // Ensure modal is at least hidden and detached cleanly.
            try { this.removeAttribute("open"); } catch {}
          }

          // Always clean up flags, even if restore threw
          if (fromBackGesture) {
            QA_HISTORY.clearSessionFlags();
          } else {
            const anyOpen = !!document.querySelector('quick-add-modal.af-quickadd[open]');
            if (!anyOpen) QA_HISTORY.clearSessionFlags();
          }
        }

        hide() {
          // Already closed (e.g. closed while an add was in flight): still run the
          // close, product-form.js waits for its modalClosed to open the drawer.
          if (!this.hasAttribute("open")) {
            if (!this.__afExitTimer) this._hideInternal(false);
            return;
          }

          // just added: let "ADDED ✓" show first (af-quick-add.js)
          const wait = (this.__afHoldUntil || 0) - Date.now();
          if (wait > 0) {
            if (!this.__afHoldTimer) {
              this.__afHoldTimer = setTimeout(() => {
                this.__afHoldTimer = 0;
                this.__afHoldUntil = 0;
                this.hide();
              }, wait);
            }
            return;
          }
          if (this.__afExitTimer) return; // already closing

          // Disarm history entry, then close
          if (!isDesktop() && QA_HISTORY.armed && !QA_HISTORY.suppressDisarm) {
            QA_HISTORY.disarmSafely();
          }

          const quick = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
          if (quick) {
            this._hideInternal(false);
            return;
          }
          this.classList.add("is-closing");
          this.__afExitTimer = setTimeout(() => {
            this.__afExitTimer = 0;
            this._hideInternal(false);
          }, isDesktop() ? 160 : 220);
        }
      }
    );
  }

  /* =========================================================
     Click handler: ONLY your quick add buttons
     ========================================================= */
  document.addEventListener(
    "click",
    (e) => {
      const btn = closestFromEvent(e, "button.af-quickadd__submit[data-product-url]");
      if (!btn) return;

      const openerEl = btn.closest("modal-opener");
      const modalSelector = openerEl?.getAttribute("data-modal");
      if (!modalSelector) return;

      const modal = document.querySelector(modalSelector);
      if (!modal) return;

      e.preventDefault();
      e.stopPropagation();

      QA_HISTORY.suppressDisarm = true;
      closeOthers(modal);
      QA_HISTORY.suppressDisarm = false;

      if (typeof modal.show === "function") {
        modal.show(btn);

        if (!modal.__afQaManaged) {
          QA_HISTORY.arm();

          AF_SCROLLLOCK.lock();
          if (!modal.hasAttribute("open")) modal.setAttribute("open", "");
          loadIntoModal(modal, btn);
        }
        return;
      }

      if (!modal.hasAttribute("open")) modal.setAttribute("open", "");
      QA_HISTORY.arm();

      AF_SCROLLLOCK.lock();
      loadIntoModal(modal, btn);
    },
    true
  );

  /* =========================================================
     Unlock backstop for native modals when they close
     + If sentinel remains after native close, remove it SAFELY (no navigation)
     ========================================================= */
  const syncLockToOpenState = () => {
    const anyOpen = !!document.querySelector("quick-add-modal.af-quickadd[open]");

    if (anyOpen) {
      AF_SCROLLLOCK.lock();
      return;
    }

    AF_SCROLLLOCK.unlock();

    QA_HISTORY.clearSessionFlags();
  };

  const openObserver = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === "attributes" && m.attributeName === "open") {
        syncLockToOpenState();
        break;
      }
    }
  });

  const observeModal = (modal) => {
    if (!modal) return;
    try {
      openObserver.observe(modal, { attributes: true, attributeFilter: ["open"] });
    } catch (e) {}
  };

  const scanForModals = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll("quick-add-modal.af-quickadd").forEach(observeModal);
  };

  // Initial scan
  const bootObserveModals = () => {
    scanForModals(document);
    syncLockToOpenState();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootObserveModals);
  } else {
    bootObserveModals();
  }

  // Observe only additions; avoid rescanning the whole document on every mutation
  let pendingModalScan = false;
  const scheduleModalScanSync = () => {
    if (pendingModalScan) return;
    pendingModalScan = true;
    requestAnimationFrame(() => {
      pendingModalScan = false;
      syncLockToOpenState();
    });
  };

  /* =========================================================
     Prefetch triggers
     ========================================================= */

  document.addEventListener(
    "pointerenter",
    (e) => {
      const btn = closestFromEvent(e, "button.af-quickadd__submit[data-product-url]");
      if (!btn) return;
      prefetchFromBtn(btn);
    },
    true
  );

  document.addEventListener(
    "touchstart",
    (e) => {
      const btn = closestFromEvent(e, "button.af-quickadd__submit[data-product-url]");
      if (!btn) return;
      prefetchFromBtn(btn);
    },
    { passive: true, capture: true }
  );

  const HAS_IO = "IntersectionObserver" in window;

  // Viewport prefetch is throttled: at most PREFETCH_MAX low-priority fetches
  // at once, newest-visible first, and a card scrolled past before its turn is
  // skipped. Previously every card within 300px fetched at once (~50 KB each),
  // so on mobile data the product actually tapped queued behind them all.
  const PREFETCH_MAX = 2;
  const nearView = new Set();
  const queue = [];
  let inflight = 0;

  const lowData = () => {
    const c = navigator.connection;
    return !!(c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || "")));
  };

  const pump = () => {
    while (inflight < PREFETCH_MAX && queue.length) {
      const btn = queue.pop();
      if (!btn.isConnected || !nearView.has(btn)) continue;
      const url = btnUrl(btn);
      if (!url || freshEntry(url)) continue;
      inflight++;
      prefetchFromBtn(btn, "low").finally(() => {
        inflight--;
        pump();
      });
    }
  };

  const io = HAS_IO
    ? new IntersectionObserver(
        (entries) => {
          for (const ent of entries) {
            const btn = ent.target;
            if (!ent.isIntersecting) {
              nearView.delete(btn);
              continue;
            }
            nearView.add(btn);
            if (lowData()) continue;
            const i = queue.indexOf(btn);
            if (i !== -1) queue.splice(i, 1);
            queue.push(btn);
          }
          pump();
        },
        { rootMargin: "300px" }
      )
    : null;

  const observedBtns = new WeakSet();

  const observeBtn = (b) => {
    if (!b || observedBtns.has(b)) return;
    observedBtns.add(b);
    io.observe(b);
  };

  const scanForButtons = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    scope
      .querySelectorAll("button.af-quickadd__submit[data-product-url]")
      .forEach(observeBtn);
  };

  if (HAS_IO) {
    const bootObserveButtons = () => scanForButtons(document);

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", bootObserveButtons);
    } else {
      bootObserveButtons();
    }
  }

  /* =========================================================
     Single document-wide observer for injected nodes:
     watches newly added modals (open-state sync) AND newly added
     quick-add buttons (viewport prefetch). Observe only additions;
     avoid rescanning the whole document on every mutation.
     ========================================================= */
  const injectObserver = new MutationObserver((muts) => {
    let found = false;
    for (const m of muts) {
      for (const n of m.addedNodes) {
        if (!(n instanceof Element)) continue;

        // 1. injected quick-add modals -> watch their [open] attribute
        if (n.matches?.("quick-add-modal.af-quickadd")) {
          observeModal(n);
          found = true;
        } else {
          // scan only within the added subtree
          const inner = n.querySelectorAll?.("quick-add-modal.af-quickadd");
          if (inner && inner.length) {
            inner.forEach(observeModal);
            found = true;
          }
        }

        // 2. injected quick-add buttons -> queue viewport prefetch
        if (HAS_IO) {
          if (n.matches?.("button.af-quickadd__submit[data-product-url]")) {
            observeBtn(n);
          } else {
            scanForButtons(n);
          }
        }
      }
    }
    if (found) scheduleModalScanSync();
  });
  injectObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
